import type { DocMetadataActionRequest, DocMetadataActionResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation } from "@tanstack/react-query";

import { workbenchDocumentsApi } from "../../../api/client";
import { useAuth } from "../../../hooks/useAuth";
import { sendCoord } from "../coord/channel";

/**
 * Applies a check-in / move-document action to a single document. Shared by the
 * check-in checkbox and the OK button in `MoveDocumentRow` so both go through
 * the same in-flight mutation.
 *
 * On success, broadcasts a `doc-action-applied` coordination message
 * (regardless of which fields were sent) so the opener window's docs &
 * messages list refreshes. This runs here — not in each call site — so no
 * future caller of this hook can forget to trigger the refresh. When the
 * request checks the document in, the message also carries the acting
 * analyst's oraid (parsed from the authenticated user's `oraid@DOMAIN`-shaped
 * `userName`) for an optimistic `reviewedBy` patch, since the BFF's action
 * response has no document fields to patch with.
 * @param appId - Approval id the document belongs to.
 * @param documentId - The document id.
 * @returns {ReturnType<typeof useMutation<DocMetadataActionResponse, unknown, DocMetadataActionRequest>>} The TanStack mutation object.
 */
export function useApplyDocumentAction(appId: string, documentId: string) {
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (body: DocMetadataActionRequest): Promise<DocMetadataActionResponse> => {
      const response = await workbenchDocumentsApi.applyDocAction(appId, documentId, body);
      return response.data;
    },
    onSuccess: (_response, request) => {
      const message = {
        type: "doc-action-applied" as const,
        appId,
        docId: documentId,
        request,
        reviewedBy: request.checkIn ? user?.userName?.split("@")[0] : undefined,
      };
      sendCoord(message);
    },
  });
}
